import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllStores, createStore, updateStore, deleteStore, SERVER_BASE_URL } from '../api/thamanService'; 
import Loader from '../components/Loader';
import ErrorMessage from '../components/ErrorMessage';
import StoreModal from '../components/StoreModal';

export default function StoresPage() {
    const [stores, setStores] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStore, setEditingStore] = useState(null);

    const getStores = async () => {
        try {
            setError(null);
            setIsLoading(true);
            const data = await fetchAllStores();
            setStores(data);
        } catch (err) {
            setError('Failed to fetch stores. Make sure the API server is running.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getStores();
    }, []);

    // --- THIS FUNCTION IS NOW SIMPLER ---
    // It just passes the data object directly to the service functions.
    const handleSaveStore = async (storeData) => {
        try {
            if (editingStore) {
                // Pass the ID and the data object
                await updateStore(editingStore.id, storeData);
            } else {
                // Pass the data object
                await createStore(storeData);
            }
            getStores(); // Refresh the list of stores
        } catch (err) {
            setError('Failed to save the store. Please try again.');
            // Re-throw the error so the modal knows the save failed
            throw err;
        }
    };

    const handleDeleteStore = async (storeId) => {
        if (window.confirm('Are you sure you want to delete this store?')) {
            try {
                await deleteStore(storeId);
                getStores();
            } catch (err) {
                setError('Failed to delete the store.');
            }
        }
    };

    const handleOpenModal = (store = null) => {
        setEditingStore(store);
        setIsModalOpen(true);
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">Stores</h1>
                <button 
                    onClick={() => handleOpenModal()}
                    className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700"
                >
                    + Add Store
                </button>
            </div>

            {isLoading && <Loader message="Fetching stores..." />}
            {error && <ErrorMessage message={error} />}
            
            {!isLoading && !error && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <ul className="divide-y divide-gray-200">
                        {stores.length > 0 ? (
                            stores.map(store => (
                                <li key={store.id} className="py-4 flex items-center">
                                    <img 
                                        src={store.logo ? `${SERVER_BASE_URL}${store.logo}` : 'https://placehold.co/100x100?text=No+Logo'} 
                                        alt={`${store.english_name} logo`}
                                        className="w-16 h-16 object-contain rounded-md mr-4"
                                    />
                                    <Link to={`/stores/${store.id}/flyers`} className="flex-grow">
                                        <div className="hover:bg-gray-50 p-2 rounded-md">
                                            <p className="text-lg font-medium text-gray-900">{store.english_name}</p>
                                            <p className="text-sm text-gray-500">{store.arabic_name}</p>
                                        </div>
                                    </Link>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm text-gray-500 mr-4">{store.flyers_count || 0} Flyers</span>
                                        <button onClick={() => handleOpenModal(store)} className="text-blue-600 hover:text-blue-800">Edit</button>
                                        <button onClick={() => handleDeleteStore(store.id)} className="text-red-600 hover:text-red-800">Delete</button>
                                    </div>
                                </li>
                            ))
                        ) : (
                            <p className="text-center text-gray-500 py-4">No stores found. Add one to get started!</p>
                        )}
                    </ul>
                </div>
            )}

            <StoreModal 
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleSaveStore}
                initialData={editingStore}
            />
        </div>
    );
}